/**
 * **菜单栈** —— push / pop / 叠加层。**纯状态机，不碰 Pixi。**
 *
 * 和 `hand/` 那边同一个分法：状态机在这儿（能在 node 里断言），
 * 挂载和绘制在 `screenHost.ts`。菜单最容易出错的地方不是"画得对不对"，
 * 而是**层级和覆盖关系**（谁盖住谁、ESC 该关掉哪一层、被盖住的那层还能不能点），
 * 这些全都能用数据断言，不该靠肉眼看。
 *
 * ## 两种层，语义完全不同
 *
 * | | 屏幕栈 `screens` | 叠加层 `overlays` |
 * | --- | --- | --- |
 * | 语义 | **独占**：一次只看得到最上面那一个 | **附加**：下面的一切照常显示 |
 * | 用途 | 标题 → 汇合 → 房间 | 设置、确认框、玩家详情 |
 * | push 时 | 上一个**隐藏** | 下面的**继续渲染** |
 * | ESC | 退回上一个 | 关掉最上面这层 |
 *
 * 「设置页要能叠加在任意界面（菜单/游戏）之上」这条要求，就是 `overlays`
 * 这一列存在的全部理由 —— 它是**附加**的，所以底下是菜单还是游戏都无所谓，
 * 栈本身不需要知道底下是什么。
 *
 * ## 几个刻意的规定
 *
 * - **根屏幕不可弹出**：`screens` 永远至少有一个。菜单没有"全空"的状态，
 *   否则画面会是一片黑，而且 ESC 无处可去。
 * - **push 屏幕会清空叠加层**：叠加层是盖在**某一块屏幕**上的，
 *   底下那块都换掉了，它的叠加层就没有上下文了。
 * - **重复打开同一个叠加层 = 提到最上面**，不会叠出两份。
 */

import { t } from '../i18n';

export type ScreenId = string;

/** 屏幕栈与叠加层的当前内容 */
export interface StackState {
  /**
   * 独占的屏幕栈。**末尾是最上层**，也是唯一可见的那个。
   * 约定 `screens[0]` 是根（标题页），不可弹出。
   */
  readonly screens: readonly ScreenId[];
  /** 附加的叠加层。**末尾是最上层**。空数组 = 没有被盖住 */
  readonly overlays: readonly ScreenId[];
}

/** 屏幕栈最大深度。防的是"某个按钮把自己 push 进去"这种死循环 */
export const MAX_SCREEN_DEPTH = 16;
/** 叠加层最大深度。两层已经够用（设置 + 确认框），8 是纯保险 */
export const MAX_OVERLAY_DEPTH = 8;

/** 建一个以 `rootId` 为根的栈 */
export function createStack(rootId: ScreenId): StackState {
  return { screens: [rootId], overlays: [] };
}

/** 当前唯一可见的那块屏幕 */
export function topScreen(state: StackState): ScreenId {
  const id = state.screens[state.screens.length - 1];
  // 根不可弹出 ⇒ 这个数组不可能为空。真为空说明有人绕过了这个模块
  if (id === undefined) throw new Error(t('stack.empty'));
  return id;
}

/** 最上面的叠加层。没有就返回 `null` */
export function topOverlay(state: StackState): ScreenId | null {
  return state.overlays[state.overlays.length - 1] ?? null;
}

/** 有没有东西盖在上面。游戏/菜单用它决定要不要停掉自己的输入 */
export function isCovered(state: StackState): boolean {
  return state.overlays.length > 0;
}

/** 某个屏幕/叠加层现在是不是在最上面（决定它要不要收指针） */
export function isOnTop(state: StackState, id: ScreenId): boolean {
  if (state.overlays.length > 0) return topOverlay(state) === id;
  return topScreen(state) === id;
}

/** 还能不能退回上一个屏幕 */
export function canPopScreen(state: StackState): boolean {
  return state.screens.length > 1;
}

/** 压入一块屏幕。会**清空叠加层**（见文件头）。超过深度上限时原样返回 */
export function pushScreen(state: StackState, id: ScreenId): StackState {
  if (state.screens.length >= MAX_SCREEN_DEPTH) return state;
  // 已经在栈里就把它以上的都弹掉，而不是压出重复的一份 ——
  // 否则"标题 → 汇合 → 设置里点了个回标题"会越堆越深
  const at = state.screens.indexOf(id);
  if (at >= 0) {
    return { screens: state.screens.slice(0, at + 1), overlays: [] };
  }
  return { screens: [...state.screens, id], overlays: [] };
}

/** 退回上一个屏幕。根屏幕不可弹出，所以到根就原样返回 */
export function popScreen(state: StackState): StackState {
  if (!canPopScreen(state)) return state;
  return { screens: state.screens.slice(0, -1), overlays: [] };
}

/** 打开一个叠加层。已经在里面就提到最上面 */
export function openOverlay(state: StackState, id: ScreenId): StackState {
  const without = state.overlays.filter((existing) => existing !== id);
  if (without.length >= MAX_OVERLAY_DEPTH) return state;
  return { screens: state.screens, overlays: [...without, id] };
}

/**
 * 关掉叠加层。
 *
 * - 不给 `id` = 关掉**最上面**那层（ESC 的行为）
 * - 给了 `id` = 关掉指定那层（"取消"按钮的行为）
 */
export function closeOverlay(state: StackState, id?: ScreenId): StackState {
  if (state.overlays.length === 0) return state;
  if (id === undefined) return { screens: state.screens, overlays: state.overlays.slice(0, -1) };
  const next = state.overlays.filter((existing) => existing !== id);
  if (next.length === state.overlays.length) return state;
  return { screens: state.screens, overlays: next };
}

/** 一次全关掉 */
export function closeAllOverlays(state: StackState): StackState {
  if (state.overlays.length === 0) return state;
  return { screens: state.screens, overlays: [] };
}

/**
 * **ESC 干一件事**：有关着的就先关，没有就退回上一屏。
 *
 * 这是全局唯一一处"按下返回该干嘛"的判断 —— 放在纯函数里，
 * 就不会出现"设置里按 ESC 结果把整个页面退掉了"这类只在手速快时复现的 bug。
 */
export function escape(state: StackState): StackState {
  if (state.overlays.length > 0) return closeOverlay(state);
  return popScreen(state);
}

export function toggleOverlay(state: StackState, id: ScreenId): StackState {
  return state.overlays.includes(id) ? closeOverlay(state, id) : openOverlay(state, id);
}

/**
 * **这一帧该画出什么。** 宿主（`screenHost.ts`）只读这个结果，
 * 它自己不做任何层级判断 —— 层级逻辑全在上面那几个函数里。
 */
export interface LayerPlan {
  /** 唯一可见的屏幕 */
  readonly screen: ScreenId;
  /** 叠加层，**从上到下**排列（绘制时反过来加） */
  readonly overlays: readonly ScreenId[];
  /** 屏幕当前被盖住 —— 它应该停掉自己的输入/动画 */
  readonly covered: boolean;
}

export function plan(state: StackState): LayerPlan {
  return {
    screen: topScreen(state),
    overlays: [...state.overlays].reverse(),
    covered: isCovered(state),
  };
}
